import React from "react";
import t from "../../../../i18n";
import type { ColumnMetaData } from "@ui/Table/ColumnMetaData";
import type { Column } from "./useColumns";
import { useColumns } from "./useColumns";
import type { ClassificationQuantity } from "../../../../api/useClassificationQuantities";
import Details from "@ui/Details";
import { useTranslations } from "../../../../api/useTranslations";

const useMetadata = () => {
  const translations = useTranslations();
  const [visibleColumns] = useColumns({});
  const deferredVisibleColumns = React.useDeferredValue(visibleColumns);
  const hidden = React.useCallback(
    (column: Column) => !deferredVisibleColumns.includes(column),
    [deferredVisibleColumns],
  );

  const metadata = React.useMemo(() => {
    const tableMeta: {
      [key in Column]: ColumnMetaData<ClassificationQuantity>;
    } = {
      name: {
        label: t("setup.paragraphs.quantities.table.definitions.column.name"),
        render: q => q.name,
        minWidth: "250px",
        sticky: true,
        hidden: hidden("name"),
      },
      projectName: {
        label: t("administration.quantities.table.header.project_name"),
        render: q => q.project?.name,
        minWidth: "200px",
        ellipsis: true,
        hidden: hidden("projectName"),
      },
      projectType: {
        label: t("administration.quantities.table.header.project_type", translations),
        render: q => q.project.typeId ?? undefined,
        minWidth: "200px",
        ellipsis: true,
        hidden: hidden("projectType"),
      },
      projectSite: {
        label: t("administration.quantities.table.header.project_site"),
        minWidth: "200px",
        render: q => q.project.siteId,
        hidden: hidden("projectSite"),
      },
      description: {
        label: t("setup.paragraphs.quantities.table.definitions.column.description"),
        render: q => (q.description === null ? undefined : <Details text={q.description} />),
        minWidth: "200px",
        hidden: hidden("description"),
      },
      unit: {
        label: t("setup.paragraphs.quantities.table.definitions.column.unit"),
        render: q => q.unit,
        minWidth: "90px",
        hidden: hidden("unit"),
      },
      system: {
        label: t("setup.paragraphs.quantities.table.definitions.column.system"),
        render: q => q.systemId ?? undefined,
        minWidth: "250px",
        hidden: hidden("system"),
      },
      equipmentType: {
        label: t("setup.paragraphs.quantities.table.definitions.column.equipmentType"),
        render: q => q.equipmentTypeId ?? undefined,
        minWidth: "250px",
        hidden: hidden("equipmentType"),
      },
      nature: {
        label: t("setup.paragraphs.quantities.table.definitions.column.type"),
        render: q => (q.sensorId ? "Mesure" : "Calcul"),
        minWidth: "110px",
        hidden: hidden("nature"),
      },
      value: {
        label: t("setup.paragraphs.quantities.table.definitions.column.formula"),
        render: q => q.currentFormula ?? undefined,
        minWidth: "400px",
        hidden: hidden("value"),
      },
      tagDataSource: {
        label: t("setup.paragraphs.quantities.table.definitions.column.tagDataSource"),
        render: q => q.sensor?.tag ?? undefined,
        minWidth: "260px",
        hidden: hidden("tagDataSource"),
      },
    };

    return tableMeta;
  }, [hidden, translations]);
  return metadata;
};

export default useMetadata;
